import { ListItemText, Stack, Typography } from "@mui/material";

import { parseStringToDate } from "~/modules/app/util/dateHelpers";
import { formatTime } from "~/modules/app/util/formatters";
import ObservationStatus from "~/modules/mapEditor/components/ObservationStatus/ObservationStatus";
import PlannerAreaStatusChip from "~/shared/components/PlannerAreaStatusChip";
import RenderIf from "~/shared/components/RenderIf";
import { splitGqlUnion } from "~/shared/helpers/splitGqlUnion";

import {
    MapEditorFeatureList_EcologicalFunctionFragment,
    MapEditorFeatureList_FeatureFragment,
    MapEditorFeatureList_MediaPointFragment,
    MapEditorFeatureList_ObservationFragment,
    MapEditorFeatureList_PlannerAreaFragment,
} from "./fragments.generated";

function MediaPointFeature({ data }: { data: MapEditorFeatureList_MediaPointFragment }) {
    return (
        <ListItemText>
            <Typography>{data.media.name}</Typography>
        </ListItemText>
    );
}

function ObservationFeature({ data }: { data: MapEditorFeatureList_ObservationFragment }) {
    return (
        <Stack direction="row" alignItems="center" width="1">
            <ListItemText>
                <Typography variant="body2">{formatTime(parseStringToDate(data.createdAt))}</Typography>
                <Typography>{data.species.name}</Typography>
            </ListItemText>
            <RenderIf payload={data.status} render={status => <ObservationStatus status={status} />} />
        </Stack>
    );
}

function EcologicalFunctionFeature({ data }: { data: MapEditorFeatureList_EcologicalFunctionFragment }) {
    return (
        <Stack direction="row" alignItems="center" width="1">
            <ListItemText>
                <Typography variant="body2">{formatTime(parseStringToDate(data.createdAt))}</Typography>
                <Typography>{data.speciesList.map(speciesListItem => speciesListItem.name).join(", ")}</Typography>
            </ListItemText>
        </Stack>
    );
}

function OtherFeature({ feature }: { feature: MapEditorFeatureList_FeatureFragment }) {
    return (
        <ListItemText>
            {Object.entries(feature.properties).map(([key, val]) => (
                <Typography key={key} variant="caption" color="text.secondary" sx={{ display: "block", whiteSpace: "pre-wrap" }}>
                    {key}: {val as string}
                </Typography>
            ))}
        </ListItemText>
    );
}

function PlannerAreaFeature({ data }: { data: MapEditorFeatureList_PlannerAreaFragment }) {
    return (
        <ListItemText>
            <Stack alignItems="flex-start">
                <Typography>{data.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {data.address}
                </Typography>
                <PlannerAreaStatusChip plannerAreaStatus={data.plannerAreaStatus} />
            </Stack>
        </ListItemText>
    );
}

export default function MapEditorFeatureListItem({ feature }: { feature: MapEditorFeatureList_FeatureFragment }) {
    return splitGqlUnion(feature.data, {
        GqlObservation: data => <ObservationFeature data={data} />,
        GqlPlannerArea: data => <PlannerAreaFeature data={data} />,
        GqlMediaPoint: data => <MediaPointFeature data={data} />,
        GqlEcologicalFunction: data => <EcologicalFunctionFeature data={data} />,
        GqlMapEditorFeatureDataTypeOther: () => <OtherFeature feature={feature} />,
    });
}
